/**
 * Per-crowd DIFFICULTY, measured rather than guessed. PRESENTATION LAYER ONLY.
 *
 * Every crowd in a scenario is landable, so "which one is hard" is not a property
 * of the level design that anyone can eyeball: it is how BIG that crowd's win
 * region is inside the space of edits a player can actually make. The level sim
 * (`src/sim/level-sim.ts`, `npm run sim:levels`) already measures exactly that.
 * It draws a deterministic 3000-edit sample per scenario from the real tool
 * vocabulary and reports, per crowd, the fraction of that sample which LANDS.
 * That fraction is the difficulty.
 *
 * The numbers are transcribed from a sim run and live with the crowd they
 * describe, as `landingFraction` on each entry of a level's `crowdChrome`. They
 * are DATA, not a model: nothing here re-derives them, and nothing in scoring
 * reads them. Re-run `npm run sim:levels` after a tuning change and copy the
 * "sample land" column across; the test in `tests/crowd-difficulty.test.ts`
 * fails if a shipped crowd is missing one, so a new level cannot ship without
 * a measured number. This module only turns a fraction into a tier.
 *
 * WHY NOT COMPUTE IT AT RUNTIME: the sim resolves ~20k edit graphs per scenario.
 * That is a build-time measurement, not something to run on a phone at load.
 */

import { crowdChromeForPack } from "../levels/index.js";

/** Difficulty tiers, easiest first. */
export type CrowdDifficulty = "easy" | "tricky" | "hard";

/**
 * Tier cut-offs on the sim's landing fraction. Chosen to match how the sample
 * actually falls (see the table): the served crowds and the other broad tastes
 * sit above 0.4, the specific-but-reachable tastes land in the single-to-low
 * double digits, and the two genuine needle-threaders are under 5%.
 */
export const DIFFICULTY_THRESHOLDS = { easy: 0.4, tricky: 0.05 } as const;

/** Player-facing label + the one-line explanation behind it. */
export interface CrowdDifficultyRead {
  tier: CrowdDifficulty;
  label: string;
  /** Landing fraction from the sim, 0..1. */
  fraction: number;
  /** Honest, concrete gloss: how often a rough cut happens to land them. */
  detail: string;
}

const LABELS: Record<CrowdDifficulty, string> = {
  easy: "Easy",
  tricky: "Tricky",
  hard: "Hard",
};

/** Tier for a measured landing fraction. */
export function difficultyForFraction(fraction: number): CrowdDifficulty {
  if (fraction >= DIFFICULTY_THRESHOLDS.easy) return "easy";
  if (fraction >= DIFFICULTY_THRESHOLDS.tricky) return "tricky";
  return "hard";
}

/**
 * "1 in N" phrasing for a landing fraction. Deliberately vague at the low end —
 * "1 in 111" reads as false precision for a 3000-sample estimate, so anything
 * rarer than 1 in 50 is described as such.
 */
function oddsPhrase(fraction: number): string {
  if (fraction <= 0) return "almost nothing lands them";
  const n = Math.round(1 / fraction);
  if (n <= 1) return "almost any honest cut lands them";
  if (n > 50) return "fewer than 1 in 50 cuts land them";
  return `about 1 in ${n} cuts lands them`;
}

/**
 * Measured difficulty for one crowd, or `null` when the scenario has no sim
 * numbers yet (a new level in progress). Callers render nothing on null rather
 * than inventing a tier.
 */
export function crowdDifficulty(packId: string | undefined, crowdId: string): CrowdDifficultyRead | null {
  const fraction = crowdChromeForPack(packId).find((c) => c.id === crowdId)?.landingFraction;
  if (typeof fraction !== "number") return null;
  const tier = difficultyForFraction(fraction);
  return { tier, label: LABELS[tier], fraction, detail: oddsPhrase(fraction) };
}
